import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, MenuItem, TextField } from '@mui/material';
import { getIn, useFormik } from 'formik';
import { Duration } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { array, boolean, number, object, string } from 'yup';
import { applyServerErrors } from '~/api/helpers';
import { useAssignPropertyGroup, useRemovePropertyGroup } from '~/api/property-groups';
import { useUpdateShowTags } from '~/api/show-tags';
import { useUpdateShow } from '~/api/shows';
import { DetailSelect, DetailSwitch, DetailTextField } from '~/components/forms/details';
import { NumberInput } from '~/components/inputs';
import { ChannelGuideSelect } from '~/components/inputs/ChannelGuideSelect';
import type { FormTableData } from '~/components/settings';
import { useAppContext } from '~/contexts';
import { PropertyOwnerType } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { ShowSettingsProps } from '../components/settings-form';
import { leagues } from './leagues';

const getFcPropertyGroups = (t: (key: string) => string) => [
  {
    id: -1,
    name: t('pages.shows.detail.settings.fanconnect_heading'),
  },
];

const fanconnectDefaults = {
  apiKey: '',
  autoschedule: false,
  domain: 'admin.fanconnect.tv',
  leagueName: '',
  subdomain: '',
};

interface PropertyGroupFormData {
  id: number;
  name: string;
  propertyGroupId: number;
  fields: FormTableData[];
}

const stripTypename = <T extends { __typename: string }>(object: T | null | undefined) => {
  if (object == null) return null;
  const { __typename, ...rest } = object;
  return rest;
};

const validationSchema = object({
  channelGuideId: number().nullable(),
  defaultImageDuration: number()
    .nullable()
    .optional()
    .min(5)
    .max(60 * 60),
  defaultViewId: number().label('Default View').required(),
  homeViewId: number().label('Home View').nullable().optional(),
  fanconnect: object({
    apiKey: string().label('API Key').required(),
    autoschedule: boolean().label('Autoschedule').required(),
    domain: string().label('Domain').required(),
    leagueName: string()
      .label('League')
      .oneOf(leagues.map(({ value }) => value))
      .required(),
    subdomain: string().label('Subdomain').required(),
  }).nullable(),
  propertyValuesAttributes: array(
    object({
      id: number().required(),
      value: string().nullable(),
    }),
  ),
});

export const useSettingsForm = ({
  propertyGroups,
  show,
  showTags,
  networkDefaultImageDuration,
}: ShowSettingsProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [updateTags] = useUpdateShowTags({
    // there is something weird happening with the mutation response here.
    // it is doing the same thing as other places. the mutation response looks good.
    // you can see in apollo dev tools that the cache gets updated, but the query data
    // does NOT get updated. cannot reproduce locally, so attempting to brute force it.
    refetchQueries: ['ShowSettings'],
    awaitRefetchQueries: true,
  });
  const [updateShow] = useUpdateShow();

  const [assignPropertyGroup] = useAssignPropertyGroup();
  const [removePropertyGroup] = useRemovePropertyGroup();
  const [confirm, confirmDialogProps] = useConfirmDialog();
  const [showAddPropertyDialog, setShowAddPropertyDialog] = useState(false);

  const defaultImageDuration = show.defaultImageDuration ?? null;

  const initialValues = useMemo(
    () => ({
      channelGuideId: show.channelGuide?.id || -1,
      defaultImageDuration: defaultImageDuration
        ? Duration.fromISO(defaultImageDuration).as('seconds')
        : null,
      defaultViewId: show.defaultView.id,
      homeViewId: show.homeView?.id || null,
      fanconnect: stripTypename(show.fanconnect),
      propertyValues: show.propertyGroups
        .flatMap((x) => x.propertyValues)
        .map(({ id, propertyDefinition, value }) => ({
          id,
          value: value || propertyDefinition.defaultValue,
        })),
    }),
    [defaultImageDuration, show],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const newValues = validationSchema.cast(values);

      const durationSeconds: number | undefined = newValues.defaultImageDuration ?? undefined;

      const defaultImageDuration =
        durationSeconds !== undefined
          ? Duration.fromObject({ seconds: durationSeconds }).toISO()
          : null;

      const result = await updateShow({
        variables: {
          showId: show.id,
          patch: {
            ...newValues,
            channelGuideId: newValues.channelGuideId === -1 ? null : newValues.channelGuideId,
            defaultImageDuration,
          },
        },
      });
      applyServerErrors(result, setErrors);
    },
    validateOnBlur: true,
    validationSchema,
  });

  const onHandleTagChange = useCallback(
    async (taggableId: number, tags: string[]) => {
      try {
        await updateTags({
          variables: {
            taggableId,
            tags,
          },
        });
      } catch (error) {
        console.error('Failed to update tags:', error);
      }
    },
    [updateTags],
  );

  const handleTagChange = useCallback(
    async (_event: React.SyntheticEvent, newValue: string[]) => {
      try {
        await onHandleTagChange(show.id, newValue);
      } catch (error) {
        console.error('Failed to handle tag change:', error);
      }
    },
    [onHandleTagChange, show.id],
  );

  const generalSettings = useMemo(() => {
    return [
      {
        heading: t('pages.shows.detail.settings.default_image_duration_label'),
        subHeading: t('pages.shows.detail.settings.default_image_duration_description'),
        dataField: (
          <NumberInput
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('defaultImageDuration', val)}
            value={formik.values.defaultImageDuration}
            helperText={t('pages.shows.detail.settings.default_image_duration_helper', {
              duration: networkDefaultImageDuration,
            })}
            min={5}
            max={60 * 60}
          />
        ),
      },
      {
        heading: t('pages.shows.detail.settings.channel_guide_label'),
        subHeading: t('pages.shows.detail.settings.channel_guide_description'),
        dataField: (
          <ChannelGuideSelect
            disabled={formik.isSubmitting}
            displayEmpty
            name="channelGuideId"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.channelGuideId}
          />
        ),
      },
      {
        heading: t('common.labels.tags'),
        subHeading: t('pages.shows.detail.settings.tags_description'),
        dataField: (
          <Autocomplete
            multiple
            autoHighlight
            disableCloseOnSelect
            freeSolo
            options={showTags ?? []}
            value={Array.from(show.tags)}
            onChange={handleTagChange}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderOption={(props, option, { selected }) => (
              <Box component="li" {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('pages.shows.detail.settings.tags_select_placeholder')}
              />
            )}
          />
        ),
      },
    ];
  }, [formik, showTags, show.tags, handleTagChange, networkDefaultImageDuration, t]);

  const viewSettings = useMemo(() => {
    if (!currentNetwork.canManageViews.value) return [];

    return [
      {
        heading: t('pages.shows.detail.settings.default_view_label'),
        subHeading: t('pages.shows.detail.settings.default_view_description'),
        dataField: (
          <DetailSelect
            disabled={formik.isSubmitting}
            name="defaultViewId"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder={t('pages.shows.detail.settings.default_view_placeholder')}
            value={formik.values.defaultViewId}
          >
            {show.views.map((view) => (
              <MenuItem key={view.id} value={view.id}>
                {view.name}
              </MenuItem>
            ))}
          </DetailSelect>
        ),
      },
      {
        heading: t('pages.shows.detail.settings.home_view_label'),
        subHeading: t('pages.shows.detail.settings.home_view_description'),
        dataField: (
          <DetailSelect
            disabled={formik.isSubmitting}
            name="homeViewId"
            onBlur={formik.handleBlur}
            onChange={(event) => {
              const value = event.target.value === '' ? null : Number(event.target.value);
              void formik.setFieldValue('homeViewId', value);
            }}
            value={formik.values.homeViewId ?? ''}
          >
            <MenuItem value="">{t('pages.shows.detail.settings.home_view_none')}</MenuItem>
            {show.views.map((view) => (
              <MenuItem key={view.id} value={view.id}>
                {view.name}
              </MenuItem>
            ))}
          </DetailSelect>
        ),
      },
    ];
  }, [formik, show.views, currentNetwork.canManageViews.value, t]);

  const addPropertyGroup = useCallback(
    async ({ id }: { id: string }) => {
      const propertyGroupId = Number(id);

      // Handle FanConnect group
      if (propertyGroupId === -1) {
        await formik.setFieldValue('fanconnect', fanconnectDefaults, true);
        setShowAddPropertyDialog(false);
        return;
      }

      const result = await assignPropertyGroup({
        variables: {
          ownerId: show.id,
          ownerType: PropertyOwnerType.Show,
          propertyGroupId,
        },
      });

      if (!result.errors?.length) {
        setShowAddPropertyDialog(false);
      }
    },
    [assignPropertyGroup, formik, show.id],
  );

  const getPropertyGroupValue = useCallback(
    (id: number) => formik.values.propertyValues.find((x) => x.id === id)?.value,
    [formik],
  );

  const setPropertyGroupValue = useCallback(
    (id: number, value: string) => {
      const index = formik.values.propertyValues.findIndex((x) => x.id === id);
      void formik.setFieldValue(`propertyValues.${index}`, {
        id,
        value: String(value),
      });
    },
    [formik],
  );

  const propertyGroupSettings: PropertyGroupFormData[] = useMemo(
    () =>
      show.propertyGroups.map((pg) => ({
        id: pg.id,
        name: pg.name,
        propertyGroupId: pg.propertyGroupId,
        fields:
          pg.propertyValues.length === 0
            ? [{ heading: `No ${pg.name} settings` }]
            : pg.propertyValues.map(({ id, propertyDefinition }) => ({
                heading: propertyDefinition.label,
                subHeading: propertyDefinition.helpText,
                dataField:
                  propertyDefinition.kind === 'boolean' ? (
                    <DetailSwitch
                      checked={getPropertyGroupValue(id) === 'true'}
                      color="primary"
                      disabled={formik.isSubmitting}
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.checked))}
                    />
                  ) : propertyDefinition.allowedValues != null ? (
                    <DetailSelect
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.value))}
                      value={getPropertyGroupValue(id)}
                    >
                      <MenuItem value="">(None)</MenuItem>
                      {propertyDefinition.allowedValues.map(({ label, value }, index) => (
                        <MenuItem key={index} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </DetailSelect>
                  ) : (
                    <DetailTextField
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.value))}
                      value={getPropertyGroupValue(id)}
                    />
                  ),
              })),
      })),
    [show, formik, getPropertyGroupValue, setPropertyGroupValue],
  );

  const deletePropertyGroup = useCallback(
    async (propertyGroup: { propertyGroupId: number }) => {
      if (!(await confirm())) return;
      await removePropertyGroup({
        variables: {
          ownerId: show.id,
          ownerType: PropertyOwnerType.Show,
          propertyGroupId: propertyGroup.propertyGroupId,
        },
      });
    },
    [confirm, show, removePropertyGroup],
  );

  const fanconnectSettings: FormTableData[] = useMemo(
    () => [
      {
        heading: t('pages.shows.detail.settings.fanconnect.autoschedule_label'),
        subHeading: t('pages.shows.detail.settings.fanconnect.autoschedule_description'),
        dataField: (
          <DetailSwitch
            disabled={formik.isSubmitting}
            name="fanconnect.autoschedule"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            checked={formik.values.fanconnect?.autoschedule}
            color="primary"
            value={true}
          />
        ),
      },
      {
        heading: t('pages.shows.detail.settings.fanconnect.league_label'),
        subHeading: t('pages.shows.detail.settings.fanconnect.league_description'),
        dataField: (
          <DetailSelect
            disabled={formik.isSubmitting}
            name="fanconnect.leagueName"
            value={formik.values.fanconnect?.leagueName}
            onChange={formik.handleChange}
          >
            {leagues.map(({ key, value }) => (
              <MenuItem key={key} value={value}>
                {key}
              </MenuItem>
            ))}
          </DetailSelect>
        ),
      },
      {
        heading: t('pages.shows.detail.settings.fanconnect.domain_label'),
        subHeading: t('pages.shows.detail.settings.fanconnect.domain_description'),
        dataField: (
          <DetailTextField
            name="fanconnect.domain"
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={String(getIn(formik.values, 'fanconnect.domain'))}
          />
        ),
      },
      {
        heading: t('pages.shows.detail.settings.fanconnect.subdomain_label'),
        subHeading: t('pages.shows.detail.settings.fanconnect.subdomain_description'),
        dataField: (
          <DetailTextField
            name="fanconnect.subdomain"
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={String(getIn(formik.values, 'fanconnect.subdomain'))}
          />
        ),
      },
      {
        heading: t('pages.shows.detail.settings.fanconnect.api_key_label'),
        subHeading: t('pages.shows.detail.settings.fanconnect.api_key_description'),
        dataField: (
          <DetailTextField
            name="fanconnect.apiKey"
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={String(getIn(formik.values, 'fanconnect.apiKey'))}
          />
        ),
      },
    ],
    [formik, t],
  );
  const filteredPropertyGroups: ReadonlyArray<{ id: number; name: string }> = [
    ...(formik.values.fanconnect == null ? getFcPropertyGroups(t) : []),
    ...propertyGroups.filter(
      ({ id }) => !show.propertyGroups.some(({ propertyGroupId }) => id === propertyGroupId),
    ),
  ];

  return {
    addPropertyGroup,
    confirmDialogProps,
    deletePropertyGroup,
    fanconnectSettings,
    filteredPropertyGroups,
    formik,
    generalSettings,
    propertyGroupSettings,
    setShowAddPropertyDialog,
    showAddPropertyDialog,
    viewSettings,
  };
};
